import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
} from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { useTranslation } from 'react-i18next'
import { createTenantUser, renameTenantUser, TenantUserInfo } from '../../redux/actions/dashboard'
import { generatePassword, validateTenantPass } from './SetPasswordDialog'

// rovnako ako backend (TenantService.ValidateLoginAsync)
const isValidLogin = (login: string) => Boolean(login.trim()) && !/\s/.test(login.trim())

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 360,
        display: 'block',
    },
}))

interface Props {
    open: boolean
    // undefined = novy login, inak premenovanie
    user?: TenantUserInfo
    // tenant noveho loginu (pre superadmina, ostatnym ho nastavi backend)
    tenantId?: string
    tenantName?: string
    onClose: () => void
    onSaved: () => void
    onError: (msg: string) => void
}

export default function TenantUserDialog({ open, user, tenantId, tenantName, onClose, onSaved, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [login, setLogin] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [touched, setTouched] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setLogin(user?.login ?? '')
        setPassword('')
        setShowPassword(false)
        setTouched(false)
    }, [open, user])

    const isNew = !user
    const loginError = touched && !isValidLogin(login)
    const passwordError = isNew && password.length > 0 && !validateTenantPass(password)

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        setTouched(true)
        if (!isValidLogin(login) || (isNew && !validateTenantPass(password))) return
        setSaving(true)
        try {
            if (isNew) {
                await createTenantUser(login.trim(), password, tenantId)
            } else {
                await renameTenantUser(user!.id, login.trim())
            }
            onSaved()
        } catch {
            onError(t('dashboard.errorSavingLogin'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>
                {isNew ? t('dashboard.addLogin') : t('dashboard.editLogin')}
                {tenantName ? ` – ${tenantName}` : ''}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    required
                    className={classes.field}
                    fullWidth
                    variant="filled"
                    label={t('dashboard.login')}
                    value={login}
                    inputProps={{ maxLength: 256 }}
                    error={loginError}
                    helperText={loginError ? t('dashboard.invalidLogin') : ' '}
                    onChange={(e) => setLogin(e.target.value)}
                />
                {isNew ? (
                    <TextField
                        required
                        className={classes.field}
                        fullWidth
                        variant="filled"
                        label={t('common.newPassword')}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        error={passwordError}
                        helperText={passwordError ? t('dashboard.errorPassValidation') : t('dashboard.setPasswordHint')}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                ) : null}
            </DialogContent>
            <DialogActions>
                {isNew ? (
                    <Button
                        onClick={() => {
                            setPassword(generatePassword())
                            setShowPassword(true)
                        }}
                        disabled={saving}
                    >
                        {t('dashboard.generatePassword')}
                    </Button>
                ) : null}
                <div style={{ flex: 1 }} />
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || (isNew && !validateTenantPass(password))}
                >
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
